import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ObjectService } from '../api/ObjectService';
import { useUnityBuildStore } from '../stores/store';
import { useStore } from 'zustand';
import {
  GenerateGETSignedUrlRequest,
  Object as ProtoObject,
} from '../api/protosCompiled/object/object_pb';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from '../stores/localStorage';
import { sortByPageNum } from '../utils/sortByPageNum';

// Define ExtendedObject type with previewUrl
export type ExtendedObject = ProtoObject.AsObject & {
  previewUrl: string;
};

// Simplified status type for document processing
export type DocumentStatus = {
  isProcessing: boolean;
  isPolling: boolean;
  processedCount: number;
  totalCount: number;
  lastUpdated: Date;
  newDocumentsDetected: boolean;
  recentUpload: boolean; // Flag to track if we've recently uploaded documents
};

export function useFetchObjects() {
  const sessionToken = useStore(useLocalStorage).sessionToken;
  const { selectedProject } = useStore(useUnityBuildStore);
  const queryClient = useQueryClient();

  // Keep track of previous document list to detect new additions
  const previousDocumentsRef = useRef<string[]>([]);

  // Polling control
  const [shouldPoll, setShouldPoll] = useState(false);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const MAX_POLL_COUNT = 40; // Maximum polls (40 × 3s = ~2 minutes)

  const [documentStatus, setDocumentStatus] = useState<DocumentStatus>({
    isProcessing: false,
    isPolling: false,
    processedCount: 0,
    totalCount: 0,
    lastUpdated: new Date(),
    newDocumentsDetected: false,
    recentUpload: false,
  });

  // Function to start polling after a document upload
  const startPollingForNewDocuments = useCallback(() => {
    setShouldPoll(true);
    setDocumentStatus(prev => ({
      ...prev,
      isPolling: true,
      newDocumentsDetected: false,
      recentUpload: true, // Mark that we've recently uploaded documents
    }));
    pollCountRef.current = 0;
  }, []);

  // Function to stop polling
  const stopPolling = useCallback(() => {
    setShouldPoll(false);
    setDocumentStatus(prev => ({ ...prev, isPolling: false }));
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Query for fetching objects
  const { data, error, isLoading, refetch } = useQuery<ExtendedObject[]>(
    ['availableObjects', selectedProject.id],
    async (): Promise<ExtendedObject[]> => {
      if (selectedProject.id) {
        setDocumentStatus(prev => ({
          ...prev,
          isProcessing: true,
          lastUpdated: new Date(),
        }));

        const response = await ObjectService.getObjectListByProjectId(selectedProject.id);

        const totalCount = response.objectsList.length;
        setDocumentStatus(prev => ({ ...prev, totalCount }));

        // Check for new documents by comparing with previous list
        const currentDocuments = response.objectsList.map(obj => obj.objectname);
        const newDocuments = currentDocuments.filter(
          docName => !previousDocumentsRef.current.includes(docName)
        );

        // If we've found new documents and had previous documents
        if (newDocuments.length > 0 && previousDocumentsRef.current.length > 0) {
          // Update local state for immediate UI feedback
          setDocumentStatus(prev => ({
            ...prev,
            newDocumentsDetected: true,
          }));

          // Stop polling when new documents are detected
          stopPolling();
        }

        // Update previous documents list
        previousDocumentsRef.current = currentDocuments;

        const objects = await Promise.all(
          response.objectsList.map(async (obj, index) => {
            const req = new GenerateGETSignedUrlRequest();
            req.setMethod('GET');
            req.setGcsobjectname(obj.gcsobjectname);
            req.setSessiontoken(sessionToken);

            try {
              const previewUrl = await ObjectService.handleGETGCSRequest_Preview(
                req,
                obj.objectname
              );
              setDocumentStatus(prev => ({
                ...prev,
                processedCount: index + 1,
                lastUpdated: new Date(),
              }));
              return {
                ...obj,
                previewUrl,
              };
            } catch (error) {
              console.error(`Error fetching preview for ${obj.objectname}:`, error);
              setDocumentStatus(prev => ({
                ...prev,
                processedCount: index + 1,
                lastUpdated: new Date(),
              }));
              return {
                ...obj,
                previewUrl: '',
              };
            }
          })
        );

        setDocumentStatus(prev => ({
          ...prev,
          isProcessing: false,
          // Reset newDocumentsDetected after we've successfully processed everything
          newDocumentsDetected: false,
          // Keep recentUpload flag so we know to continue checking on dropdown opens
          recentUpload: prev.recentUpload,
        }));

        // Use sortByPageNum instead of alphabetical sorting
        return sortByPageNum(objects) as ExtendedObject[];
      }
      throw new Error('No valid ID available for fetching objects');
    },
    {
      enabled: !!selectedProject.id,
      onError: error => {
        console.error('Error fetching objects:', error);
        setDocumentStatus(prev => ({ ...prev, isProcessing: false }));
      },
      refetchInterval: undefined,
      staleTime: 60000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Handle polling with timeout
  useEffect(() => {
    if (shouldPoll) {
      if (pollCountRef.current >= MAX_POLL_COUNT) {
        console.log('Reached maximum polling attempts, stopping');
        stopPolling();
        return;
      }

      pollTimeoutRef.current = setTimeout(() => {
        refetch();
        pollCountRef.current++;
      }, 3000); // Poll every 3 seconds

      return () => {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
        }
      };
    }
  }, [shouldPoll, refetch, stopPolling, data]);

  const refetchObjects = () => {
    queryClient.invalidateQueries(['availableObjects', selectedProject.id]);
  };

  // Function to mark all documents as observed (clear the new documents indicator)
  const markAllAsObserved = useCallback(() => {
    setDocumentStatus(prev => ({
      ...prev,
      newDocumentsDetected: false,
      recentUpload: false,
    }));
  }, []);

  return {
    data,
    error,
    isLoading,
    refetchObjects,
    documentStatus,
    startPollingForNewDocuments,
    stopPolling,
    markAllAsObserved,
  };
}

// Update object name
export function useUpdateObjectName() {
  const { selectedProject } = useStore(useUnityBuildStore);
  const queryClient = useQueryClient();
  return useMutation(
    (data: FilteredProtoObject) => ObjectService.updateObjectName(data.id, data.objectname),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['availableObjects', selectedProject.id]);
      },
      onError: error => {
        console.error('Error updating object name:', error);
      },
    }
  );
}

type FilteredProtoObject = Omit<
  ProtoObject.AsObject,
  'projectid' | 'gcsobjectname' | 'userscalesList' | 'openaifileid' | 'hastakeoff'
>;
